import { useEffect, useRef, useState } from 'react';
import {
  Combobox,
  Field,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Option,
  TagGroup,
  tokens,
  type TagGroupProps,
} from '@fluentui/react-components';

const selectAllValue = '__select_all__';

export type MultiSelectOption = {
  value: string;
  label: string;
};

export type MultiSelectProps = {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
};

const MultiSelect = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select',
}: MultiSelectProps) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollToSelection = useRef(false);
  const queryText = query.trim().toLowerCase();
  const visibleOptions = options.filter((option) =>
    option.label.toLowerCase().includes(queryText)
  );
  const visibleValues = visibleOptions.map((option) => option.value);
  const allVisibleSelected =
    visibleValues.length > 0 &&
    visibleValues.every((value) => selected.includes(value));
  const someVisibleSelected = visibleValues.some((value) =>
    selected.includes(value)
  );
  const selectedOptions = options.filter((option) =>
    selected.includes(option.value)
  );

  useEffect(() => {
    if (!open) {
      scrollToSelection.current = false;
      return;
    }
    if (!scrollToSelection.current || queryText) return;
    const list = listRef.current;
    if (!list) return;
    const selectedItem = [
      ...list.querySelectorAll<HTMLElement>('[aria-checked="true"]'),
    ].find((item) => (item.textContent ?? '').trim() !== 'Select all');
    scrollToSelection.current = false;
    if (!selectedItem) return;
    const listTop = list.getBoundingClientRect().top;
    const itemTop = selectedItem.getBoundingClientRect().top;
    list.scrollTop += itemTop - listTop;
  }, [open, queryText, visibleOptions]);

  const dismiss: TagGroupProps['onDismiss'] = (_event, data) => {
    onChange(selected.filter((value) => value !== data.value));
  };

  return (
    <Field label={label}>
      <Combobox
        multiselect
        inlinePopup
        positioning={{ autoSize: 'width' }}
        style={{ width: '100%', minWidth: 0 }}
        listbox={{
          ref: listRef,
          style: { maxHeight: 240, overflowY: 'auto' },
        }}
        placeholder={open ? 'Search' : placeholder}
        open={open}
        value={open ? query : ''}
        selectedOptions={
          allVisibleSelected ? [selectAllValue, ...selected] : selected
        }
        onOpenChange={(_, data) => {
          setOpen(data.open);
          if (data.open) {
            scrollToSelection.current = true;
          } else {
            setQuery('');
          }
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onOptionSelect={(_, data) => {
          if (data.optionValue === selectAllValue) {
            onChange(
              allVisibleSelected
                ? selected.filter((value) => !visibleValues.includes(value))
                : [...new Set([...selected, ...visibleValues])]
            );
            return;
          }
          onChange(
            data.selectedOptions.filter((value) => value !== selectAllValue)
          );
        }}
      >
        {visibleOptions.length > 0 ? (
          <Option
            value={selectAllValue}
            text="Select all"
            aria-checked={
              allVisibleSelected ? true : someVisibleSelected ? 'mixed' : false
            }
            checkIcon={
              someVisibleSelected && !allVisibleSelected
                ? {
                    style: {
                      backgroundColor: tokens.colorCompoundBrandBackground,
                      borderColor: tokens.colorCompoundBrandBackground,
                      color: tokens.colorNeutralForegroundInverted,
                    },
                    children: (
                      <span
                        style={{
                          width: 8,
                          height: 2,
                          backgroundColor: 'currentColor',
                        }}
                      />
                    ),
                  }
                : undefined
            }
          >
            Select all
          </Option>
        ) : (
          <Option value="__none__" text="" disabled>
            No results
          </Option>
        )}
        {visibleOptions.map((option) => (
          <Option
            key={option.value || option.label || '__blank__'}
            value={option.value}
          >
            {option.label}
          </Option>
        ))}
      </Combobox>
      {selectedOptions.length > 0 && (
        <TagGroup
          onDismiss={dismiss}
          aria-label={label}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.spacingHorizontalXS,
            marginTop: tokens.spacingVerticalS,
          }}
        >
          {selectedOptions.map((option) => (
            <InteractionTag
              key={option.value || option.label || '__blank__'}
              value={option.value}
              appearance="brand"
              size="small"
            >
              <InteractionTagPrimary hasSecondaryAction>
                {option.label}
              </InteractionTagPrimary>
              <InteractionTagSecondary aria-label="Remove" />
            </InteractionTag>
          ))}
        </TagGroup>
      )}
    </Field>
  );
};

export default MultiSelect;
